/**
 * Dependency-free marker shared by the two ends of the toast → console bridge so
 * they can't drift:
 *
 *  - `lib/toast.ts` (the WRITER) prefixes every bridged console line with
 *    `TOAST_CONSOLE_PREFIX` and passes a structured JSON payload as the second
 *    console argument.
 *  - `lib/otel/logger.ts` (a READER) uses `toastVariantFromConsoleArgs` to pull
 *    the toast's true `variant` back out of those two args so it can assign the
 *    correct Kibana severity regardless of which console method the toast bridge
 *    actually used (the bridge escalates every variant to `console.error` when
 *    Mixpanel is recording).
 *
 * Kept free of any import (no `sonner`, no `mixpanel-browser`) so both the
 * browser-only toast module and the OTel logger can share it without dragging
 * browser deps into either.
 */

/** Marker `lib/toast.ts` puts at the head of every `[toast] …` console line. */
export const TOAST_CONSOLE_PREFIX = "[toast]";

/**
 * Recover the toast variant from a patched-console call's args when they match
 * the bridge's two-arg shape: `args[0]` is a string beginning with
 * `TOAST_CONSOLE_PREFIX`, and `args[1]` is a JSON string whose parsed object has
 * a string `variant`. Returns that variant, or `null` for any non-toast /
 * malformed / missing-second-arg case (JSON.parse failures are swallowed).
 */
export function toastVariantFromConsoleArgs(args: unknown[]): string | null {
  const [first, second] = args;
  if (typeof first !== "string" || !first.startsWith(TOAST_CONSOLE_PREFIX)) {
    return null;
  }
  if (typeof second !== "string") return null;
  try {
    const parsed: unknown = JSON.parse(second);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "variant" in parsed &&
      typeof (parsed as { variant: unknown }).variant === "string"
    ) {
      return (parsed as { variant: string }).variant;
    }
    return null;
  } catch {
    return null;
  }
}
